import type { ReactNode } from 'react';

import { formatShortDate } from '@/lib/datetime';

/**
 * Renders an ISO timestamp as a compact, scannable table cell
 * ("Jun 10, 2026") using tabular figures so dates line up column-wise.
 * Absent values collapse to an em dash.
 *
 * Wired into `CrudConfig` `datetime` fields via `format` so list tables
 * stop showing the verbose `toLocaleString()` default
 * ("6/10/2026, 11:09:46 PM").
 *
 * @param value - ISO-8601 timestamp string (or null/undefined).
 * @returns A right-sized date cell node.
 */
export function renderDateCell(value: string | null | undefined): ReactNode {
  return <span className="tabular-nums">{formatShortDate(value)}</span>;
}

/**
 * Renders a numeric value right-aligned with monospace tabular figures so
 * counts and point totals scan cleanly down a column. Non-numeric or
 * absent values collapse to an em dash.
 *
 * @param value - The numeric value to display.
 * @returns A right-aligned numeric cell node.
 */
export function renderNumberCell(value: number | null | undefined): ReactNode {
  const display = typeof value === 'number' ? value.toLocaleString() : '—';
  return <span className="font-mono text-sm tabular-nums">{display}</span>;
}
